import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  CurrencyInr, Scales, Package, Stack, ChartLineUp, Buildings,
  FileXls, FilePdf, Printer, FunnelSimple, Lightbulb, Warning,
  Sparkle, ShoppingCart, BowlFood, Trash, TrendUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { api, API, fmtINR, TOKEN_KEY } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const MONTHS = [
  { v: 1, label: "Jan" }, { v: 2, label: "Feb" }, { v: 3, label: "Mar" },
  { v: 4, label: "Apr" }, { v: 5, label: "May" }, { v: 6, label: "Jun" },
  { v: 7, label: "Jul" }, { v: 8, label: "Aug" }, { v: 9, label: "Sep" },
  { v: 10, label: "Oct" }, { v: 11, label: "Nov" }, { v: 12, label: "Dec" },
];

const PIE_COLORS = ["#0A3B24", "#D4AF37", "#7A9E5C", "#16A34A", "#A66B00", "#0E5232", "#B45309", "#475569", "#9333EA", "#BE185D", "#0EA5E9", "#65A30D"];

const SEVERITY_STYLE = {
  info: { bg: "bg-[#F0FDF4]", border: "border-[#16A34A]/30", icon: Lightbulb, color: "#16A34A" },
  warning: { bg: "bg-[#FEF3C7]", border: "border-[#D4AF37]/40", icon: Warning, color: "#D4AF37" },
  opportunity: { bg: "bg-[#EFF6FF]", border: "border-[#0A3B24]/30", icon: Sparkle, color: "#0A3B24" },
};

export default function MonthlySpendAnalysis() {
  const { guard } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("All");
  const [month, setMonth] = useState("All");
  const [productionFor, setProductionFor] = useState("All");
  const [brand, setBrand] = useState("All");
  const [outlet, setOutlet] = useState("All");
  const [ingCategory, setIngCategory] = useState("All");

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (year !== "All") p.set("year", year);
    if (month !== "All") p.set("month", month);
    if (productionFor !== "All") p.set("production_for", productionFor);
    if (brand !== "All") p.set("brand", brand);
    if (outlet !== "All") p.set("outlet", outlet);
    if (ingCategory !== "All") p.set("ingredient_category", ingCategory);
    return p.toString();
  }, [year, month, productionFor, brand, outlet, ingCategory]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/production/v2/monthly-analysis?${params}`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) toast.error("Failed to load monthly analysis"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params]);

  const dl = guard(async (kind) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const url = `${API}/production/v2/monthly-analysis/export/${kind}?${params}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const ext = kind === "pdf" ? "pdf" : "xlsx";
      const label = kind.replace("/", "_");
      a.download = `KOSTRA_Monthly_${label}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Downloaded");
    } catch { toast.error("Download failed"); }
  }, "download report");

  if (loading && !data) return <div className="py-12 text-center text-sm text-[#4B5563]">Loading monthly analysis...</div>;
  if (!data) return null;

  const o = data.overview;
  const af = data.available_filters || {};
  const hasPlans = (o.plan_count || 0) > 0;

  return (
    <div className="space-y-8" data-testid="monthly-spend-analysis">
      {/* Filters */}
      <section className="kostra-card p-5" data-testid="msa-filters">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="kostra-section-title text-lg inline-flex items-center gap-2">
            <FunnelSimple size={18} weight="duotone" /> Filters
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => dl("excel/spend")} disabled={!hasPlans}
                    className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-9 text-xs"
                    data-testid="msa-dl-spend">
              <FileXls size={13} weight="bold" className="mr-1.5" /> Monthly Spend (Excel)
            </Button>
            <Button onClick={() => dl("excel/consumption")} disabled={!hasPlans}
                    className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-9 text-xs"
                    data-testid="msa-dl-consumption">
              <FileXls size={13} weight="bold" className="mr-1.5" /> Consumption (Excel)
            </Button>
            <Button onClick={() => dl("pdf")} disabled={!hasPlans}
                    className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold h-9 text-xs"
                    data-testid="msa-dl-pdf">
              <FilePdf size={13} weight="bold" className="mr-1.5" /> Monthly Analysis (PDF)
            </Button>
            <Button onClick={() => window.print()} variant="outline" className="h-9 text-xs" data-testid="msa-print">
              <Printer size={13} weight="bold" className="mr-1.5" /> Print
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <FilterSelect label="Year" value={year} onChange={setYear} options={["All", ...(af.years || [])]} testid="msa-filter-year" />
          <FilterSelect label="Month" value={month} onChange={setMonth} options={["All", ...MONTHS.map(m => String(m.v))]} labelFor={(v) => v === "All" ? "All" : MONTHS.find(m => String(m.v) === v)?.label} testid="msa-filter-month" />
          <FilterSelect label="Production For" value={productionFor} onChange={setProductionFor} options={["All", ...(af.production_for || [])]} testid="msa-filter-for" />
          <FilterSelect label="Brand" value={brand} onChange={setBrand} options={["All", ...(af.brands || [])]} testid="msa-filter-brand" />
          <FilterSelect label="Outlet" value={outlet} onChange={setOutlet} options={["All", ...(af.outlets || [])]} testid="msa-filter-outlet" />
          <FilterSelect label="Ingredient Category" value={ingCategory} onChange={setIngCategory} options={["All", ...(af.ingredient_categories || [])]} testid="msa-filter-category" />
        </div>
      </section>

      {!hasPlans && (
        <div className="kostra-card p-10 text-center" data-testid="msa-empty">
          <Package size={40} weight="duotone" className="mx-auto text-[#0A3B24]" />
          <div className="text-sm font-semibold text-[#111827] mt-2">No production plans match these filters</div>
          <div className="text-xs text-[#4B5563] mt-1">Save plans in the Production Plan builder to unlock spend analytics.</div>
        </div>
      )}

      {hasPlans && (
        <>
          {/* Section 1: KPI Overview */}
          <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="msa-overview-kpis">
            <Kpi label="Total Production Cost" value={fmtINR(o.total_cost)} icon={CurrencyInr} accent="#0A3B24" />
            <Kpi label="Total Weight" value={`${o.total_weight_kg.toFixed(2)} kg`} icon={Scales} accent="#D4AF37" />
            <Kpi label="Plans" value={o.plan_count} icon={Package} accent="#0E5232" />
            <Kpi label="Recipes Produced" value={o.recipes_produced} icon={BowlFood} accent="#16A34A" />
            <Kpi label="Avg Daily Cost" value={fmtINR(o.avg_daily_cost)} icon={ChartLineUp} accent="#A66B00" />
            <Kpi label="Avg Cost / Kg" value={fmtINR(o.avg_cost_per_kg)} icon={Stack} accent="#7A9E5C" />
          </section>

          {/* AI Insights */}
          {(data.insights || []).length > 0 && (
            <section className="kostra-card p-5 md:p-6" data-testid="msa-insights">
              <h3 className="kostra-section-title text-lg inline-flex items-center gap-2">
                <Sparkle size={18} weight="duotone" className="text-[#D4AF37]" /> AI Insights
              </h3>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.insights.map((ins, i) => {
                  const s = SEVERITY_STYLE[ins.severity] || SEVERITY_STYLE.info;
                  const Icon = s.icon;
                  return (
                    <div key={i} className={`rounded-lg border ${s.border} ${s.bg} p-3 flex items-start gap-3`} data-testid={`msa-insight-${i}`}>
                      <div className="h-8 w-8 rounded-md bg-white/70 flex items-center justify-center shrink-0" style={{ color: s.color }}>
                        <Icon size={16} weight="duotone" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-[#0A3B24]">{ins.title}</div>
                        <div className="text-xs text-[#4B5563] mt-1">{ins.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Section 6: Monthly Trend */}
          {(data.monthly_trend || []).length > 0 && (
            <section className="kostra-card p-5 md:p-6" data-testid="msa-monthly-trend">
              <h3 className="kostra-section-title text-lg inline-flex items-center gap-2">
                <TrendUp size={18} weight="duotone" /> Monthly Trend
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                <div className="h-64">
                  <div className="text-xs text-[#4B5563] mb-1">Cost (₹)</div>
                  <ResponsiveContainer width="100%" height="90%">
                    <LineChart data={data.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 6 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="production_cost" stroke="#0A3B24" strokeWidth={2} name="Production Cost" dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="ingredient_cost" stroke="#D4AF37" strokeWidth={2} name="Ingredient Cost" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="h-64">
                  <div className="text-xs text-[#4B5563] mb-1">Weight (kg) &amp; Plans</div>
                  <ResponsiveContainer width="100%" height="90%">
                    <BarChart data={data.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 6 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar yAxisId="left" dataKey="production_weight_kg" fill="#0A3B24" name="Weight (kg)" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="right" dataKey="plan_count" fill="#D4AF37" name="Plans" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>
          )}

          {/* Section 5: Category Spend */}
          {(data.category_spend || []).length > 0 && (
            <section className="kostra-card p-5 md:p-6" data-testid="msa-category-spend">
              <h3 className="kostra-section-title text-lg inline-flex items-center gap-2">
                <Buildings size={18} weight="duotone" /> Category-wise Spend
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.category_spend}
                        dataKey="total_spend"
                        nameKey="category"
                        innerRadius={50}
                        outerRadius={100}
                        paddingAngle={2}
                      >
                        {data.category_spend.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 6 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                        <th className="py-2 pr-2">Category</th>
                        <th className="py-2 pr-2 text-right">Items</th>
                        <th className="py-2 pr-2 text-right">Spend</th>
                        <th className="py-2 pl-2 text-right">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.category_spend.map((c, i) => (
                        <tr key={c.category} className="border-b border-[#F1F2F4]" data-testid={`msa-cat-row-${i}`}>
                          <td className="py-2 pr-2 font-semibold text-[#0A3B24] inline-flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                            {c.category}
                          </td>
                          <td className="py-2 pr-2 text-right font-mono-num">{c.items_count}</td>
                          <td className="py-2 pr-2 text-right font-mono-num font-semibold">{fmtINR(c.total_spend)}</td>
                          <td className="py-2 pl-2 text-right font-mono-num">{c.share_pct.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {/* Section 2 + 3: Ingredient Consumption + Spend (merged into one table) */}
          {(data.ingredient_spend || []).length > 0 && (
            <section className="kostra-card p-5 md:p-6" data-testid="msa-ingredients">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="kostra-section-title text-lg inline-flex items-center gap-2">
                  <Stack size={18} weight="duotone" /> Ingredient Consumption &amp; Spend
                </h3>
                <span className="gold-pill text-[11px] font-semibold px-2.5 py-1 rounded-full">
                  Total: {fmtINR(data.total_ingredient_spend || 0)}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                      <th className="py-2 pr-2">#</th>
                      <th className="py-2 pr-2">Ingredient</th>
                      <th className="py-2 pr-2">Category</th>
                      <th className="py-2 pr-2 text-right">Consumed</th>
                      <th className="py-2 pr-2 text-right">Avg / Day</th>
                      <th className="py-2 pr-2 text-right">Rate / Kg</th>
                      <th className="py-2 pl-2 text-right">Total Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ingredient_spend.slice(0, 25).map((it, i) => (
                      <tr key={i} className="border-b border-[#F1F2F4]" data-testid={`msa-ing-row-${i}`}>
                        <td className="py-2 pr-2 font-mono-num text-[#4B5563]">{i + 1}</td>
                        <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{it.name}</td>
                        <td className="py-2 pr-2 text-xs text-[#4B5563]">{it.category}</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{it.total_consumed_kg.toFixed(2)} kg</td>
                        <td className="py-2 pr-2 text-right font-mono-num text-[#4B5563]">{it.avg_daily_kg.toFixed(2)} kg</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(it.rate_per_kg)}</td>
                        <td className="py-2 pl-2 text-right font-mono-num font-bold text-[#0A3B24]">{fmtINR(it.total_spend)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(data.top_10_costly?.length || 0) > 0 && (
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-[#E5E7EB] p-3 bg-[#FDF9EE]/40">
                    <div className="text-xs font-bold text-[#0A3B24] uppercase tracking-wider mb-2">Top Cost Contributors</div>
                    {data.top_10_costly.slice(0, 5).map((it, i) => (
                      <div key={i} className="flex items-center justify-between py-1 border-b last:border-0 border-[#E5E7EB]/50 text-xs">
                        <span className="text-[#0A3B24] font-semibold truncate">{i + 1}. {it.name}</span>
                        <span className="font-mono-num text-[#0A3B24]">{fmtINR(it.total_spend)}</span>
                      </div>
                    ))}
                  </div>
                  {(data.lowest_5_costly?.length || 0) > 0 && (
                    <div className="rounded-lg border border-[#E5E7EB] p-3">
                      <div className="text-xs font-bold text-[#4B5563] uppercase tracking-wider mb-2">Lowest Cost Contributors</div>
                      {data.lowest_5_costly.map((it, i) => (
                        <div key={i} className="flex items-center justify-between py-1 border-b last:border-0 border-[#E5E7EB]/50 text-xs">
                          <span className="text-[#4B5563] truncate">{it.name}</span>
                          <span className="font-mono-num text-[#4B5563]">{fmtINR(it.total_spend)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Section 4: Purchase Planning */}
          {(data.purchase_planning || []).length > 0 && (
            <section className="kostra-card p-5 md:p-6" data-testid="msa-purchase-planning">
              <h3 className="kostra-section-title text-lg inline-flex items-center gap-2">
                <ShoppingCart size={18} weight="duotone" /> Purchase Planning Forecast
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FDF9EE] text-[#D4AF37] border border-[#D4AF37]/30 ml-1">
                  +10% buffer
                </span>
              </h3>
              <p className="text-xs text-[#4B5563] mt-0.5">Projected monthly requirement and suggested purchase quantity based on the current window.</p>
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                      <th className="py-2 pr-2">Ingredient</th>
                      <th className="py-2 pr-2">Category</th>
                      <th className="py-2 pr-2 text-right">Monthly Need</th>
                      <th className="py-2 pr-2 text-right">Suggested Buy</th>
                      <th className="py-2 pr-2 text-right">Rate / Kg</th>
                      <th className="py-2 pl-2 text-right">Est. Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.purchase_planning.slice(0, 15).map((pp, i) => (
                      <tr key={i} className="border-b border-[#F1F2F4]" data-testid={`msa-pp-row-${i}`}>
                        <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{pp.name}</td>
                        <td className="py-2 pr-2 text-xs text-[#4B5563]">{pp.category}</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{pp.monthly_usage_kg.toFixed(2)} kg</td>
                        <td className="py-2 pr-2 text-right font-mono-num font-semibold text-[#0A3B24]">{pp.suggested_purchase_kg.toFixed(2)} kg</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(pp.rate_per_kg)}</td>
                        <td className="py-2 pl-2 text-right font-mono-num font-bold text-[#0A3B24]">{fmtINR(pp.estimated_spend)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Section 7: Recipe Consumption */}
          {(data.recipe_consumption?.most_produced || []).length > 0 && (
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="msa-recipe-consumption">
              <div className="kostra-card p-5">
                <h3 className="kostra-section-title text-base inline-flex items-center gap-2">
                  <BowlFood size={16} weight="duotone" /> Most Produced Recipes
                </h3>
                <table className="w-full text-sm mt-3">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                      <th className="py-2 pr-2">Recipe</th>
                      <th className="py-2 pr-2 text-right">Qty</th>
                      <th className="py-2 pl-2 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recipe_consumption.most_produced.slice(0, 8).map((r, i) => (
                      <tr key={r.recipe_id} className="border-b border-[#F1F2F4]" data-testid={`msa-most-${i}`}>
                        <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{r.recipe_name}</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{r.qty_produced}</td>
                        <td className="py-2 pl-2 text-right font-mono-num">{fmtINR(r.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="kostra-card p-5">
                <h3 className="kostra-section-title text-base inline-flex items-center gap-2">
                  <CurrencyInr size={16} weight="duotone" /> Highest Cost Recipes
                </h3>
                <table className="w-full text-sm mt-3">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                      <th className="py-2 pr-2">Recipe</th>
                      <th className="py-2 pr-2 text-right">Qty</th>
                      <th className="py-2 pl-2 text-right">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recipe_consumption.highest_cost.slice(0, 8).map((r, i) => (
                      <tr key={r.recipe_id} className="border-b border-[#F1F2F4]" data-testid={`msa-high-${i}`}>
                        <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{r.recipe_name}</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{r.qty_produced}</td>
                        <td className="py-2 pl-2 text-right font-mono-num font-bold text-[#0A3B24]">{fmtINR(r.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Section 8: Wastage Impact */}
          {(data.wastage_impact?.rows || []).length > 0 && (
            <section className="kostra-card p-5 md:p-6" data-testid="msa-wastage">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="kostra-section-title text-lg inline-flex items-center gap-2">
                  <Trash size={18} weight="duotone" /> Wastage Impact
                </h3>
                <div className="text-xs text-[#4B5563]">
                  Total recoverable value: <strong className="text-[#DC2626] font-mono-num">{fmtINR(data.wastage_impact.summary.total_wasted_value)}</strong>
                  {" · "}Avg wastage: <strong className="text-[#0A3B24] font-mono-num">{data.wastage_impact.summary.avg_wastage_pct.toFixed(2)}%</strong>
                </div>
              </div>
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                      <th className="py-2 pr-2">Ingredient</th>
                      <th className="py-2 pr-2 text-right">Consumed</th>
                      <th className="py-2 pr-2 text-right">Wasted</th>
                      <th className="py-2 pr-2 text-right">Wastage %</th>
                      <th className="py-2 pl-2 text-right">Recoverable Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.wastage_impact.rows.slice(0, 12).map((w, i) => (
                      <tr key={i} className="border-b border-[#F1F2F4]" data-testid={`msa-waste-${i}`}>
                        <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{w.name}</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{w.total_consumed_kg.toFixed(2)} kg</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{w.wasted_kg.toFixed(2)} kg</td>
                        <td className="py-2 pr-2 text-right font-mono-num">{w.wastage_pct.toFixed(2)}%</td>
                        <td className="py-2 pl-2 text-right font-mono-num font-bold text-[#DC2626]">{fmtINR(w.recoverable_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, labelFor, testid }) {
  return (
    <div>
      <Label className="kostra-data-label">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-9 text-xs" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{labelFor ? labelFor(o) : o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="kostra-card p-4">
      <div className="flex items-start justify-between">
        <span className="kostra-data-label text-[10px]">{label}</span>
        <div className="h-8 w-8 rounded-md flex items-center justify-center" style={{ background: `${accent}10`, color: accent }}>
          <Icon size={16} weight="duotone" />
        </div>
      </div>
      <div className="mt-2 font-mono-num text-xl font-bold text-[#111827]">{value}</div>
    </div>
  );
}
